import { useEffect, useMemo, useState } from 'react'
import { usePagination, useSortBy, useTable } from 'react-table'
import Swal from 'sweetalert2'
import { useCustomReducer } from '../../../../../base/customHooks'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { exportToXLSX } from '../../../../../base/utils/dataHelper'
import InputCampaign from '../../../../../components/InputCampaign'
import IPButton from '../../../../../components/IPButton'
import IPInput from '../../../../../components/IPInput'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import { getCompanyApi } from '../../../components/api'
import {
  ShoppingCreditItem,
  ShoppingCreditListRequest,
  ShoppingCreditListResponse,
  CreditStatusOptions,
  CreditUserTypeOptions,
  PaymentTypeOptions,
} from '../../models/models'
import { GetShoppingCreditListApi } from '../../services/compayPaymentApi'
import './style.scss'
import { Link } from 'react-router-dom'

interface ShoppingCreditReportState {
  credits: ShoppingCreditListResponse | null
  filteredCredits: ShoppingCreditItem[]
  form: ShoppingCreditListRequest
  companyOptions: ISelectOption[]
  selectedCompany: ISelectOption | null
  selectedStatus: ISelectOption | null
  selectedCreditType: ISelectOption | null
  selectedUserType: ISelectOption | null
}

// Date -> 'YYYY-MM-DD' (input[type=date] formatı, yerel saate göre)
const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${year}-${month}-${day}`
}

// 'YYYY-MM-DD' -> UTC gün başlangıcı.
// Backend bitiş tarihini gün sonuna (23:59:59.999) kendisi genişletiyor, istemcide saat kaydırma yapılmaz.
const toRequestDate = (value: string): Date => new Date(`${value}T00:00:00.000Z`)

function ShoppingCreditReport(props: BaseComponentWithContextProps) {
  const inititalStartDate = new Date()
  inititalStartDate.setDate(1)
  inititalStartDate.setHours(0, 0, 0, 0)

  const [startDate, setStartDate] = useState(toDateInputValue(inititalStartDate))
  const [endDate, setEndDate] = useState(toDateInputValue(new Date()))
  const [dealerFilter, setDealerFilter] = useState('')
  const [serialFilter, setSerialFilter] = useState('')
  const [showFilterRow, setShowFilterRow] = useState(false)

  const getInitialState = (): ShoppingCreditReportState => ({
    credits: null,
    filteredCredits: [],
    form: {
      StartDate: inititalStartDate,
      EndDate: new Date(),
      Status: 'ALL',
      CreditType: 'ALL',
      DealerCode: '',
      Company: 0,
      OrderId: '',
      SerialNumber: '',
      CustomerTcNo: '',
    },
    companyOptions: [],
    selectedCompany: null,
    selectedStatus: CreditStatusOptions[0],
    selectedCreditType: PaymentTypeOptions[0],
    selectedUserType: CreditUserTypeOptions[0],
  })

  const [state, setState] = useCustomReducer<ShoppingCreditReportState>(getInitialState())

  const getCompanies = async () => {
    try {
      const res: any[] = await getCompanyApi()
      const companies = [{ label: 'Tümü', value: 0 }, ...res.map(x => ({ label: x.Name, value: x.Code }))]
      setState({ companyOptions: companies })
    } catch (err) {
      console.error('Şirketler alınırken hata:', err)
    }
  }

  useEffect(() => {
    getCompanies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!startDate || !endDate) {
      Swal.fire('Uyarı', 'Lütfen başlangıç ve bitiş tarihi seçiniz.', 'warning')
      return
    }

    if (toRequestDate(startDate) > toRequestDate(endDate)) {
      Swal.fire('Uyarı', 'Başlangıç tarihi bitiş tarihinden büyük olamaz.', 'warning')
      return
    }

    const req: ShoppingCreditListRequest = {
      StartDate: toRequestDate(startDate),
      EndDate: toRequestDate(endDate),
      Status: state.selectedStatus?.value as string,
      CreditType: state.selectedCreditType?.value as string,
      DealerCode: state.form.DealerCode || '',
      Company: (state.selectedCompany?.value as number) || 0,
      OrderId: state.form.OrderId || '',
      SerialNumber: state.form.SerialNumber || '',
      CustomerTcNo: state.form.CustomerTcNo || '',
      CreditUserType:
        state.selectedCreditType?.value === 'CART' ? (state.selectedUserType?.value as string) : undefined,
      // Sayfalama, filtreleme ve Excel çıktısı istemci tarafında yapıldığı için
      // backend'den tüm kayıtlar isteniyor. Aksi halde özet kartları tüm veriyi,
      // tablo ise yalnızca ilk sayfayı gösteriyordu.
      PageNumber: 1,
      PageSize: 0,
    }

    try {
      props.showLoading()
      const res = await new GetShoppingCreditListApi({}).getCreditList(req)
      props.hideLoading()

      if (!res.Success) {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: res.ErrorMessage || 'Veriler alınırken bir hata oluştu.',
        })
        return
      }

      setDealerFilter('')
      setSerialFilter('')
      setState({ credits: res, filteredCredits: res.Credits })
      setShowFilterRow(res.TotalCount > 0)
      // Veri olmaması bir hata değil, normal bir sonuç: modal açılmaz,
      // tablonun yerinde satır içi boş durum mesajı gösterilir.
    } catch (err: any) {
      props.hideLoading()
      Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: err.description || 'Veriler alınırken bir hata oluştu.',
      })
    }
  }

  // Filtreleme
  useEffect(() => {
    if (!state.credits) return

    let filtered = [...state.credits.Credits]

    if (dealerFilter) {
      const filter = dealerFilter.toLowerCase()
      filtered = filtered.filter(
        c =>
          (c.DealerCode || '').toLowerCase().includes(filter) || (c.DealerName || '').toLowerCase().includes(filter)
      )
    }

    if (serialFilter) {
      const filter = serialFilter.toLowerCase()
      filtered = filtered.filter(c => (c.SerialNumber || '').toLowerCase().includes(filter))
    }

    setState({ filteredCredits: filtered })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerFilter, serialFilter, state.credits])

  const formatCurrency = (amount: number, currency: string = 'TL'): string => {
    return `${amount.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`
  }

  const formatDate = (date: Date | string): string => {
    if (!date) return '-'
    const d = new Date(date)
    return d.toLocaleDateString('tr-TR') + ' ' + d.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })
  }

  // Backend iki farklı kaynaktan (TableCompayPayments / TableSerialCreditApplications) veri döndürdüğü için
  // onaylı kayıtlar hem COMPLETED hem APPROVED koduyla gelebiliyor. Her ikisi de "Onaylandı" (yeşil) gösterilir,
  // böylece liste özet kartındaki "Onaylı" sayısıyla tutarlı olur.
  const statusMap: { [key: string]: { color: string; textColor: string; text: string } } = {
    APPROVED: { color: '#28a745', textColor: 'white', text: 'Onaylandı' },
    COMPLETED: { color: '#28a745', textColor: 'white', text: 'Onaylandı' },
    SUCCESS: { color: '#28a745', textColor: 'white', text: 'Onaylandı' },
    REJECTED: { color: '#dc3545', textColor: 'white', text: 'Reddedildi' },
    FAILED: { color: '#dc3545', textColor: 'white', text: 'Başarısız' },
    PENDING: { color: '#ffc107', textColor: '#212529', text: 'Beklemede' },
    SMS_SENT: { color: '#ffc107', textColor: '#212529', text: 'SMS Gönderildi' },
    CANCELLED: { color: '#6c757d', textColor: 'white', text: 'İptal Edildi' },
    EXPIRED: { color: '#6c757d', textColor: 'white', text: 'Süresi Doldu' },
    TIMEOUT: { color: '#fd7e14', textColor: 'white', text: 'Zaman Aşımı' },
    ERROR: { color: '#fd7e14', textColor: 'white', text: 'Hata' },
  }

  const getStatusText = (status: string, statusName?: string): string =>
    statusMap[(status || '').toUpperCase()]?.text || statusName || status || '-'

  const getStatusBadge = (status: string, statusName?: string) => {
    const s = statusMap[(status || '').toUpperCase()]
    return (
      <span
        className='badge'
        style={{
          backgroundColor: s?.color || '#6c757d',
          color: s?.textColor || 'white',
          padding: '5px 10px',
          borderRadius: '4px',
          fontWeight: 500,
        }}
      >
        {getStatusText(status, statusName)}
      </span>
    )
  }

  const getCreditTypeBadge = (type: string) => {
    const typeMap: { [key: string]: { class: string; text: string } } = {
      CART: { class: 'badge-info', text: 'Sepet Kredisi' },
      SERIAL: { class: 'badge-primary', text: 'Seri No Kredisi' },
    }
    const t = typeMap[type] || { class: 'badge-secondary', text: type }
    return <span className={`badge ${t.class}`}>{t.text}</span>
  }

  const getUserTypeBadge = (type: string) => {
    const typeMap: { [key: string]: { class: string; text: string } } = {
      DEALER: { class: 'badge-dealer', text: 'Bayi' },
      CUSTOMER: { class: 'badge-customer', text: 'Müşteri' },
    }
    const t = typeMap[type] || { class: 'badge-secondary', text: type }
    return <span className={`badge ${t.class}`}>{t.text}</span>
  }

  const columns = useMemo(
    () => [
      {
        Header: 'Başvuru No',
        accessor: 'OrderId',
      },
      {
        Header: 'Bayi',
        accessor: 'DealerCode',
        Cell: ({ row }: any) => (
          <span>
            {row.original.DealerCode}
            <br />
            <small className='text-muted'>{row.original.DealerName}</small>
          </span>
        ),
      },
      {
        Header: 'Şirket',
        accessor: 'CompanyName',
      },
      {
        Header: 'Kredi Tipi',
        accessor: 'CreditType',
        Cell: ({ value }: any) => getCreditTypeBadge(value),
      },
      {
        Header: 'Kullanıcı Tipi',
        accessor: 'CreditUserType',
        Cell: ({ value }: any) => getUserTypeBadge(value),
      },
      {
        Header: 'Baz Tutar',
        accessor: 'BaseAmount',
        className: 'num',
        Cell: ({ value }: any) => formatCurrency(value, 'TL'),
      },
      {
        Header: 'Kar Marjı',
        accessor: 'DealerMarkupAmount',
        className: 'num',
        Cell: ({ value, row }: any) => {
          if (!value || value === 0) return '-'
          return (
            <span style={{ color: '#6a1b9a', fontWeight: '600' }}>
              +{formatCurrency(value, 'TL')}
              <br />
              <small>(%{row.original.DealerMarkupPercentage})</small>
            </span>
          )
        },
      },
      {
        Header: 'Son Tutar',
        accessor: 'FinalAmount',
        className: 'num',
        Cell: ({ value }: any) => (
          <span style={{ fontWeight: '600', color: '#e65100' }}>{formatCurrency(value, 'TL')}</span>
        ),
      },
      {
        Header: 'Müşteri',
        Cell: ({ row }: any) => {
          if (row.original.CreditUserType !== 'CUSTOMER') return '-'
          return (
            <span>
              {row.original.CustomerFullName || '-'}
              <br />
              <small className='text-muted'>{row.original.CustomerTcNo}</small>
            </span>
          )
        },
      },
      {
        Header: 'Seri No',
        accessor: 'SerialNumber',
        Cell: ({ value }: any) => value || '-',
      },
      {
        Header: 'Durum',
        accessor: 'Status',
        Cell: ({ row }: any) => getStatusBadge(row.original.Status, row.original.StatusName),
      },
      {
        Header: 'Tarih',
        accessor: 'CreatedAt',
        Cell: ({ value }: any) => formatDate(value),
      },
      {
        Header: 'İşlem',
        Cell: ({ row }: any) => (
          <button
            type='button'
            className='btn-detail'
            onClick={() => showCreditDetail(row.original)}
            title='Detayı görüntüle'
            aria-label='Detayı görüntüle'
          >
            <i className='fas fa-eye'></i>
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const data = useMemo(() => state.filteredCredits, [state.filteredCredits])

  // Özet ölçümler tek bir ızgarada toplanır: ana ölçümler dolu zeminli,
  // kırılım ölçümleri sol renk şeritli. Eskiden iki ayrı satırdı ve alt satır
  // neredeyse beyaz zeminde okunmuyordu.
  const metrics = useMemo(() => {
    const c = state.credits
    if (!c) return []
    return [
      { variant: 'is-solid total', icon: 'fas fa-list', label: 'Toplam Başvuru', value: `${c.TotalCount}` },
      { variant: 'is-solid success', icon: 'fas fa-check-circle', label: 'Onaylı', value: `${c.ApprovedCount}` },
      { variant: 'is-solid fail', icon: 'fas fa-times-circle', label: 'Reddedildi', value: `${c.RejectedCount}` },
      {
        variant: 'is-solid amount',
        icon: 'fas fa-lira-sign',
        label: 'Toplam Kredi Tutarı',
        value: formatCurrency(c.TotalFinalAmount, 'TL'),
      },
      {
        variant: 'is-solid markup',
        icon: 'fas fa-percentage',
        label: 'Toplam Bayi Kar Marjı',
        value: formatCurrency(c.TotalDealerMarkup, 'TL'),
      },
      { variant: 'is-outline waiting', icon: 'fas fa-clock', label: 'Beklemede', value: `${c.PendingCount}` },
      { variant: 'is-outline dealer', icon: 'fas fa-store', label: 'Bayi Kredisi', value: `${c.DealerCreditCount}` },
      {
        variant: 'is-outline customer',
        icon: 'fas fa-user',
        label: 'Müşteri Kredisi',
        value: `${c.CustomerCreditCount}`,
      },
      {
        variant: 'is-outline base',
        icon: 'fas fa-coins',
        label: 'Baz Tutar',
        value: formatCurrency(c.TotalBaseAmount, 'TL'),
      },
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.credits])

  const tableInstance = useTable(
    {
      // @ts-ignore
      columns,
      data,
      // @ts-ignore
      initialState: { pageIndex: 0, pageSize: 20 },
      // data referansı her değiştiğinde react-table sayfayı/sıralamayı sıfırlıyor;
      // bu yüzden ileri/geri tıklandığında tablo ilk sayfaya dönüyordu.
      // Sıfırlamayı biz aşağıdaki effect ile sadece veri seti gerçekten değiştiğinde yapıyoruz.
      autoResetPage: false,
      autoResetSortBy: false,
    },
    useSortBy,
    usePagination
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance as any

  // Yeni sorgu veya tablo filtresi sonrası kullanıcı boş bir sayfada kalmasın
  useEffect(() => {
    gotoPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const showCreditDetail = (credit: ShoppingCreditItem) => {
    let detailHtml = `
      <div style="text-align: left; font-size: 14px;">
        <div style="margin-bottom: 10px;"><strong>Başvuru No:</strong> ${credit.OrderId}</div>
        <div style="margin-bottom: 10px;"><strong>Bayi:</strong> ${credit.DealerCode} - ${credit.DealerName}</div>
        <div style="margin-bottom: 10px;"><strong>Şirket:</strong> ${credit.CompanyName}</div>
        <div style="margin-bottom: 10px;"><strong>Kredi Tipi:</strong> ${credit.CreditTypeName}</div>
        <div style="margin-bottom: 10px;"><strong>Kullanıcı Tipi:</strong> ${credit.CreditUserTypeName}</div>
        <hr/>
        <div style="margin-bottom: 5px; color: #1565c0;"><strong>Tutar Bilgileri</strong></div>
        <div style="margin-bottom: 10px;"><strong>Baz Tutar:</strong> ${formatCurrency(credit.BaseAmount, 'TL')}</div>
    `

    if (credit.DealerMarkupAmount && credit.DealerMarkupAmount > 0) {
      detailHtml += `
        <div style="margin-bottom: 10px;"><strong>Bayi Kar Marjı:</strong> <span style="color: #6a1b9a; font-weight: 600;">+${formatCurrency(
        credit.DealerMarkupAmount,
        'TL'
      )} (%${credit.DealerMarkupPercentage})</span></div>
      `
    }

    detailHtml += `
        <div style="margin-bottom: 10px;"><strong>Son Tutar:</strong> <span style="color: #e65100; font-weight: 600;">${formatCurrency(
      credit.FinalAmount,
      'TL'
    )}</span></div>
    `

    if (credit.CreditUserType === 'CUSTOMER') {
      detailHtml += `
        <hr/>
        <div style="margin-bottom: 5px; color: #e65100;"><strong>Müşteri Bilgileri</strong></div>
        <div style="margin-bottom: 10px;"><strong>TC Kimlik:</strong> ${credit.CustomerTcNo || '-'}</div>
        <div style="margin-bottom: 10px;"><strong>Ad Soyad:</strong> ${credit.CustomerFullName || '-'}</div>
        <div style="margin-bottom: 10px;"><strong>Telefon:</strong> ${credit.CustomerPhone || '-'}</div>
      `
    }

    if (credit.SerialNumber) {
      detailHtml += `
        <hr/>
        <div style="margin-bottom: 5px; color: #2e7d32;"><strong>Ürün Bilgileri</strong></div>
        <div style="margin-bottom: 10px;"><strong>Seri No:</strong> ${credit.SerialNumber}</div>
        <div style="margin-bottom: 10px;"><strong>Ürün Kodu:</strong> ${credit.ProductCode || '-'}</div>
        <div style="margin-bottom: 10px;"><strong>Ürün Adı:</strong> ${credit.ProductName || '-'}</div>
        <div style="margin-bottom: 10px;"><strong>Fatura No:</strong> ${credit.InvoiceNumber || '-'}</div>
      `
    }

    detailHtml += `
        <hr/>
        <div style="margin-bottom: 10px;"><strong>Durum:</strong> ${getStatusText(credit.Status, credit.StatusName)}</div>
        <div style="margin-bottom: 10px;"><strong>Onay Kodu:</strong> ${credit.AuthCode || '-'}</div>
        <div style="margin-bottom: 10px;"><strong>Referans No:</strong> ${credit.RefNum || '-'}</div>
        ${credit.ErrorMessage
        ? `<div style="margin-bottom: 10px; color: red;"><strong>Hata:</strong> ${credit.ErrorMessage}</div>`
        : ''
      }
        <div style="margin-bottom: 10px;"><strong>Başvuru Tarihi:</strong> ${formatDate(credit.CreatedAt)}</div>
        ${credit.ApprovedAt
        ? `<div style="margin-bottom: 10px;"><strong>Onay Tarihi:</strong> ${formatDate(credit.ApprovedAt)}</div>`
        : ''
      }
      </div>
    `

    Swal.fire({
      title: 'Kredi Başvuru Detayı',
      html: detailHtml,
      width: 650,
      confirmButtonText: 'Kapat',
    })
  }

  const handleExportExcel = () => {
    if (!state.filteredCredits || state.filteredCredits.length === 0) {
      Swal.fire('Uyarı', 'Dışa aktarılacak veri bulunamadı.', 'warning')
      return
    }

    const exportData = state.filteredCredits.map(c => ({
      'Başvuru No': c.OrderId,
      'Bayi Kodu': c.DealerCode,
      'Bayi Adı': c.DealerName,
      Şirket: c.CompanyName,
      'Kredi Tipi': c.CreditTypeName,
      'Kullanıcı Tipi': c.CreditUserTypeName,
      'Baz Tutar (TL)': c.BaseAmount,
      'Kar Marjı (TL)': c.DealerMarkupAmount || 0,
      'Kar Marjı (%)': c.DealerMarkupPercentage || 0,
      'Son Tutar (TL)': c.FinalAmount,
      'Müşteri TC': c.CustomerTcNo || '',
      'Müşteri Adı': c.CustomerFullName || '',
      'Müşteri Tel': c.CustomerPhone || '',
      'Seri No': c.SerialNumber || '',
      'Ürün Kodu': c.ProductCode || '',
      'Fatura No': c.InvoiceNumber || '',
      Durum: getStatusText(c.Status, c.StatusName),
      'Onay Kodu': c.AuthCode || '',
      'Başvuru Tarihi': formatDate(c.CreatedAt),
      'Onay Tarihi': c.ApprovedAt ? formatDate(c.ApprovedAt) : '',
    }))

    exportToXLSX(exportData, 'Alisveris_Kredisi_Raporu')
  }

  return (
    <div className='shopping-credit-report'>
      <div className='card'>
        <div className='card-header'>
          <h4>
            <i className='fas fa-money-bill-wave' style={{ marginRight: '10px' }}></i>
            Alışveriş Kredisi Raporu
          </h4>
          <div className='header-actions'>
            <Link to='/admin/CompayOperation/CreditContractReport' style={{ textDecoration: 'none' }}>
              <IPButton
                type='button'
                text='Sözleşme Raporu'
                className='btn-filter'
                style={{
                  background: 'rgba(255, 255, 255, 0.14)',
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                }}
              />
            </Link>
            <Link to='/admin/CompayOperation/CreditSettings' style={{ textDecoration: 'none' }}>
              <IPButton
                type='button'
                text='Kredi Ayarları'
                className='btn-filter'
                style={{
                  background: 'rgba(255, 255, 255, 0.14)',
                  border: '1px solid rgba(255, 255, 255, 0.35)',
                }}
              />
            </Link>
          </div>
        </div>
        <div className='card-body'>
          {/* Filtre Formu */}
          <form onSubmit={handleSubmit} className='filter-panel'>
            <div className='filter-grid'>
              <div className='field field-date'>
                <label className='form-label' htmlFor='StartDate'>
                  Başlangıç Tarihi
                </label>
                <InputCampaign
                  type='date'
                  id='StartDate'
                  name='StartDate'
                  label={''}
                  value={startDate}
                  max={endDate}
                  onChange={e => setStartDate(e.target.value)}
                  isActive
                />
              </div>
              <div className='field field-date'>
                <label className='form-label' htmlFor='EndDate'>
                  Bitiş Tarihi
                </label>
                <InputCampaign
                  type='date'
                  id='EndDate'
                  name='EndDate'
                  label={''}
                  value={endDate}
                  min={startDate}
                  onChange={e => setEndDate(e.target.value)}
                  isActive
                />
              </div>
              <div className='field'>
                <label className='form-label'>Şirket</label>
                <IPSelectBox
                  options={state.companyOptions}
                  value={state.selectedCompany}
                  onChangeSingle={option => setState({ selectedCompany: option })}
                  isClearable
                  placeholder='Tümü'
                />
              </div>
              <div className='field'>
                <label className='form-label'>Durum</label>
                <IPSelectBox
                  options={CreditStatusOptions}
                  value={state.selectedStatus}
                  onChangeSingle={option => setState({ selectedStatus: option })}
                  placeholder='Tümü'
                />
              </div>
              <div className='field'>
                <label className='form-label'>Kredi Tipi</label>
                <IPSelectBox
                  options={PaymentTypeOptions}
                  value={state.selectedCreditType}
                  onChangeSingle={option => setState({ selectedCreditType: option })}
                  placeholder='Tümü'
                />
              </div>
              {state.selectedCreditType?.value === 'CART' && (
                <div className='field'>
                  <label className='form-label'>Kullanıcı Tipi</label>
                  <IPSelectBox
                    options={CreditUserTypeOptions}
                    value={state.selectedUserType}
                    onChangeSingle={option => setState({ selectedUserType: option })}
                    placeholder='Tümü'
                  />
                </div>
              )}
              <div className='field'>
                <label className='form-label'>Bayi Kodu</label>
                <IPInput
                  type='text'
                  placeholder='Bayi kodu...'
                  value={state.form.DealerCode}
                  onChange={e => setState({ form: { ...state.form, DealerCode: e.target.value } })}
                />
              </div>
              <div className='field'>
                <label className='form-label'>Seri Numarası</label>
                <IPInput
                  type='text'
                  placeholder='Seri numarası...'
                  value={state.form.SerialNumber}
                  onChange={e => setState({ form: { ...state.form, SerialNumber: e.target.value } })}
                />
              </div>
              <div className='field'>
                <label className='form-label'>Müşteri TC No</label>
                <IPInput
                  type='text'
                  placeholder='TC Kimlik No...'
                  value={state.form.CustomerTcNo}
                  onChange={e => setState({ form: { ...state.form, CustomerTcNo: e.target.value } })}
                />
              </div>
            </div>

            <div className='filter-actions'>
              <IPButton
                type='button'
                text='Excel İndir'
                className='btn-filter'
                onClick={handleExportExcel}
                style={{
                  background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)',
                  border: 'none',
                }}
              />
              <IPButton
                type='submit'
                text='Sorgula'
                className='btn-filter'
                style={{
                  background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                  border: 'none',
                }}
              />
            </div>
          </form>

          <div className='report-note'>
            <i className='fas fa-circle-info'></i>
            <span>
              Bayiler <b>Alışveriş Kredisi</b> seçeneğini yalnızca <b>COMPAY_KREDI</b> alt tipine eklendiklerinde
              görebilir.
            </span>
          </div>

          {/* Özet Bilgiler */}
          {state.credits && (
            <div className='metrics-grid'>
              {metrics.map(m => (
                <div key={m.label} className={`metric ${m.variant}`}>
                  <div className='metric-icon'>
                    <i className={m.icon}></i>
                  </div>
                  <div className='metric-body'>
                    <div className='metric-value' title={m.value}>
                      {m.value}
                    </div>
                    <div className='metric-label'>{m.label}</div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tablo Filtreleri */}
          {showFilterRow && (
            <div className='table-tools'>
              <div className='table-tools-field'>
                <IPInput
                  type='text'
                  placeholder='Bayi kodu veya adı ile filtrele...'
                  value={dealerFilter}
                  onChange={e => setDealerFilter(e.target.value)}
                />
              </div>
              <div className='table-tools-field'>
                <IPInput
                  type='text'
                  placeholder='Seri no ile filtrele...'
                  value={serialFilter}
                  onChange={e => setSerialFilter(e.target.value)}
                />
              </div>
              <span className='table-tools-count'>
                Gösterilen: {state.filteredCredits.length} / {state.credits?.Credits?.length || 0} kayıt
              </span>
            </div>
          )}

          {/* Tablo */}
          {state.filteredCredits.length > 0 && (
            <>
              <div className='table-responsive'>
                <table {...getTableProps()} className='table table-striped table-hover'>
                  <thead>
                    {headerGroups.map((headerGroup: any) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map((column: any) => (
                          <th {...column.getHeaderProps(column.getSortByToggleProps())} className={column.className}>
                            {column.render('Header')}
                            <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody {...getTableBodyProps()}>
                    {page.map((row: any) => {
                      prepareRow(row)
                      return (
                        <tr {...row.getRowProps()}>
                          {row.cells.map((cell: any) => (
                            <td {...cell.getCellProps()} className={cell.column.className}>
                              {cell.render('Cell')}
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              {/* Sayfalama */}
              <div className='pagination-container'>
                <div className='page-info'>
                  <span>
                    Sayfa <strong>{pageIndex + 1}</strong> / {Math.max(pageOptions.length, 1)}
                  </span>
                  <select
                    value={pageSize}
                    onChange={e => setPageSize(Number(e.target.value))}
                    className='page-size'
                    aria-label='Sayfa başına kayıt sayısı'
                  >
                    {[10, 20, 50, 100].map(size => (
                      <option key={size} value={size}>
                        {size} Kayıt
                      </option>
                    ))}
                  </select>
                </div>
                <div className='page-nav'>
                  <button
                    type='button'
                    className='page-btn'
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                    title='İlk sayfa'
                    aria-label='İlk sayfa'
                  >
                    <i className='fas fa-angle-double-left'></i>
                  </button>
                  <button
                    type='button'
                    className='page-btn'
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                    title='Önceki sayfa'
                    aria-label='Önceki sayfa'
                  >
                    <i className='fas fa-angle-left'></i>
                  </button>
                  <button
                    type='button'
                    className='page-btn'
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                    title='Sonraki sayfa'
                    aria-label='Sonraki sayfa'
                  >
                    <i className='fas fa-angle-right'></i>
                  </button>
                  <button
                    type='button'
                    className='page-btn'
                    onClick={() => gotoPage(Math.max(pageCount - 1, 0))}
                    disabled={!canNextPage}
                    title='Son sayfa'
                    aria-label='Son sayfa'
                  >
                    <i className='fas fa-angle-double-right'></i>
                  </button>
                </div>
              </div>
            </>
          )}

          {state.credits && state.filteredCredits.length === 0 && (
            <div className='empty-state'>
              <i className='fas fa-folder-open'></i>
              <div>
                <div className='empty-state-title'>
                  {state.credits.TotalCount === 0 ? 'Kayıt bulunamadı' : 'Filtreye uyan kayıt yok'}
                </div>
                <div className='empty-state-text'>
                  {state.credits.TotalCount === 0
                    ? 'Seçilen kriterlere uygun alışveriş kredisi başvurusu yok. Tarih aralığını genişletip yeniden sorgulayın.'
                    : 'Tablo filtrelerini temizleyerek tüm kayıtları görebilirsiniz.'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default componentWithContext(ShoppingCreditReport)
